import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import every from 'lodash/every';
import Loader from '../../common/loader';
import { getMailingAddress } from '../user-profile/selectors';
import { getCustomerParty } from './selectors';
import {
  ArrowDownIcon,
  ArrowRightIcon,
  GoPaperlessContainer,
  LoaderContainer,
  OnlineProfileHeader,
  SaveCancelContainer,
  CancelButton,
  SaveDeliveryPreferencesButton,
  EditDeliveryPreferencesButton,
} from './styles';
import { getText } from '../../../utils/i18n';

import {
  getPoliciesWithLatestTerm,
  saveCustomerDeliveryPreferences,
  updateMultipleDeliveryPreferences,
} from './delivery-api.js';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router';
import { getStatementAccounts, updateStatementAccounts } from './profile-api';
import { setLoading } from './deliveryPreferencesReducer';
import CustomerDeliveryPreferences from './customer-preferences-section.js';
import NonStatementAccountPoliciesDeliveryPreferences from './non-statement-account-policies-section.js';
import StatementAccountAndUnderlyingPoliciesDeliveryPreferences from './statement-account-and-underlying-policies-section.js';
import {
  filteredNonSAPolicies,
  getUnderlyingPoliciesForEachStatementAccountToUpdate,
} from './delivery-utils.js';

export const DeliveryPreferences = (props) => {
  const {
    i18n,
    billingAccounts,
    customer,
    customerAddress,
    customerEmail,
    customerId,
    createdSinceDate,
    desktopDeliveryPreferencesDefaultExpanded,
    mobileDeliveryPreferencesDefaultExpanded,
    policiesCountLimit,
    policyStatusCd,
    systemId,
    dispatch,
    isLoading,
    policies,
    statementAccounts,
    apiResponseSizeLimit,
  } = props;
  let location = useLocation();
  const [deliveryPreferencesOpen, setDeliveryPreferencesOpen] = useState(
    window.innerWidth > 900
      ? desktopDeliveryPreferencesDefaultExpanded
      : mobileDeliveryPreferencesDefaultExpanded,
  );
  const [editingDeliveryPreferences, setEditingDeliveryPreferences] = useState(false);
  const [customerDeliveryPreference, setCustomerDeliveryPreference] = useState(
    customer.preferredDeliveryMethod,
  );

  const [statementAccountDeliveryPreference, setStatementAccountDeliveryPreference] = useState([]);

  let filteredStatementAccounts = billingAccounts.filter((account) => {
    return account.fullAccount.accountDisplayName === 'Statement';
  });
  let filteredUnderlyingAccounts = filteredStatementAccounts.map((thisAccount) => {
    return thisAccount.fullAccount.underlyingBillingAccounts.filter((account) => {
      return account.accountDisplayName !== 'Statement';
    });
  });
  let policiesMapped = policies.map((policy) => {
    return { ...policy, newPreference: policy?.insured?.preferredDeliveryMethod };
  });
  const [mappedPolicyPreference, setMappedPolicyPreference] = useState(policiesMapped);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    dispatch(setLoading(true));
    setTimeout(() => {
      if (location?.state?.prevPath === 'policyDetails') {
        document
          .getElementById('GoPaperlessContainer')
          .scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 1000);
    let filteredStatementAccounts = billingAccounts.filter((account) => {
      return account.fullAccount.accountDisplayName === 'Statement';
    });

    dispatch(
      getStatementAccounts(filteredStatementAccounts, setStatementAccountDeliveryPreference),
    );
  }, []);

  let updateDeliveryPreferences = async () => {
    //update order is as follows: customer delivery preferences changed,
    // statement accounts and underlying policies,
    // and finally any regular policies
    // follow up by getting latest statement accounts
    // and policies for revision in case they re-edit their preferences again
    let allPoliciesThatWereUpdated = mappedPolicyPreference.filter((policy) => {
      let currentPolicy = policies.find((thisPolicy) => policy.policyRef === thisPolicy.policyRef);
      return policy.newPreference !== currentPolicy.insured.preferredDeliveryMethod;
    });
    let filteredStatementAccountsToUpdate = statementAccountDeliveryPreference.filter((policy) => {
      let currentPolicy = statementAccounts.find(
        (thisPolicy) => thisPolicy.statementFeeAccountRef === policy.systemId,
      );
      return policy.deliveryPreference !== currentPolicy.preferredDeliveryMethod;
    });
    let filteredNonSAPoliciesToUpdate = filteredNonSAPolicies(
      allPoliciesThatWereUpdated,
      filteredStatementAccountsToUpdate,
      billingAccounts,
    );
    if (customer.preferredDeliveryMethod !== customerDeliveryPreference) {
      await dispatch(
        saveCustomerDeliveryPreferences(
          systemId,
          customer._revision,
          customerDeliveryPreference,
          enqueueSnackbar,
        ),
      );
    }
    if (filteredStatementAccountsToUpdate.length > 0) {
      let underlyingStatementAccountPolicies = getUnderlyingPoliciesForEachStatementAccountToUpdate(
        filteredStatementAccountsToUpdate,
        allPoliciesThatWereUpdated,
        filteredNonSAPoliciesToUpdate,
        billingAccounts,
        policies,
      );
      await dispatch(
        updateStatementAccounts(
          filteredStatementAccountsToUpdate,
          statementAccounts,
          enqueueSnackbar,
        ),
      );
      await dispatch(
        updateMultipleDeliveryPreferences(underlyingStatementAccountPolicies, enqueueSnackbar),
      );
    }
    if (filteredNonSAPoliciesToUpdate.length > 0) {
      await dispatch(
        updateMultipleDeliveryPreferences(filteredNonSAPoliciesToUpdate, enqueueSnackbar),
      );
    }
    await dispatch(
      getPoliciesWithLatestTerm(
        customerId,
        createdSinceDate,
        policiesCountLimit,
        policyStatusCd,
        apiResponseSizeLimit,
      ),
    ).then(() => {
      dispatch(setLoading(false));
    });
    await dispatch(
      getStatementAccounts(filteredStatementAccounts, setStatementAccountDeliveryPreference),
    );
  };

  const checkDisabled = () => {
    if (!editingDeliveryPreferences) {
      return true;
    }
    let filteredStatementAccountsMappedPolicyPreference = mappedPolicyPreference.filter(
      (policy) => {
        return !filteredUnderlyingAccounts.some((account) => {
          return account.some((subAccount) => {
            subAccount.arPolicy.policyRef === policy.policyRef;
          });
        });
      },
    );
    let policiesAndCustomerCommunicationPreferencesNotChanged = every(
      filteredStatementAccountsMappedPolicyPreference,
      (policy) => {
        let currentPolicy = policies.find(
          (thisPolicy) => policy.policyRef === thisPolicy.policyRef,
        );
        return (
          currentPolicy.insured.preferredDeliveryMethod === policy.newPreference &&
          customerDeliveryPreference === customer.preferredDeliveryMethod
        );
      },
    );
    let statementAccountCommunicationPreferencesNotChanged = every(statementAccounts, (account) => {
      let matched = statementAccountDeliveryPreference.find(
        (thisAccount) => thisAccount.systemId === account?.statementFeeAccountRef,
      );
      return matched.deliveryPreference === account.preferredDeliveryMethod;
    });

    return (
      policiesAndCustomerCommunicationPreferencesNotChanged &&
      statementAccountCommunicationPreferencesNotChanged
    );
  };
  return (
    <GoPaperlessContainer>
      <label htmlFor="deliveryPreferencesHeader" style={{ cursor: 'pointer' }}>
        <OnlineProfileHeader
          id="deliveryPreferencesHeader"
          onClick={() => setDeliveryPreferencesOpen(!deliveryPreferencesOpen)}
        >
          {deliveryPreferencesOpen ? <ArrowDownIcon /> : <ArrowRightIcon />} Delivery Preferences
        </OnlineProfileHeader>
      </label>
      {isLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {deliveryPreferencesOpen && !isLoading && (
        <>
          <p
            dangerouslySetInnerHTML={{
              __html: getText(
                i18n,
                'general.paperlessSubtitle',
                'Enjoy convenient delivery of your policy-related documents to your email.',
              ),
            }}
          />
          <br />
          <NonStatementAccountPoliciesDeliveryPreferences
            editingDeliveryPreferences={editingDeliveryPreferences}
            filteredUnderlyingAccounts={filteredUnderlyingAccounts}
            i18n={i18n}
            isLoading={isLoading}
            mappedPolicyPreference={mappedPolicyPreference}
            setMappedPolicyPreference={setMappedPolicyPreference}
          />
          <StatementAccountAndUnderlyingPoliciesDeliveryPreferences
            editingDeliveryPreferences={editingDeliveryPreferences}
            filteredStatementAccounts={filteredStatementAccounts}
            i18n={i18n}
            isLoading={isLoading}
            setStatementAccountDeliveryPreference={setStatementAccountDeliveryPreference}
            statementAccounts={statementAccounts}
            statementAccountDeliveryPreference={statementAccountDeliveryPreference}
          />
          <CustomerDeliveryPreferences
            customerDeliveryPreference={customerDeliveryPreference}
            customerAddress={customerAddress}
            customerEmail={customerEmail}
            editingDeliveryPreferences={editingDeliveryPreferences}
            i18n={i18n}
            isLoading={isLoading}
            setCustomerDeliveryPreference={setCustomerDeliveryPreference}
          />
          {!editingDeliveryPreferences && (
            <EditDeliveryPreferencesButton
              onClick={() => {
                setEditingDeliveryPreferences(!editingDeliveryPreferences);
              }}
            >
              Edit
            </EditDeliveryPreferencesButton>
          )}
          {editingDeliveryPreferences && (
            <SaveCancelContainer>
              <CancelButton
                onClick={() => {
                  setEditingDeliveryPreferences(!editingDeliveryPreferences);
                  setMappedPolicyPreference(policiesMapped);
                  setCustomerDeliveryPreference(customer.preferredDeliveryMethod);
                }}
              >
                Cancel
              </CancelButton>
              <SaveDeliveryPreferencesButton
                onClick={() => {
                  setEditingDeliveryPreferences(!editingDeliveryPreferences);
                  updateDeliveryPreferences();
                }}
                disabled={checkDisabled()}
              >
                Apply
              </SaveDeliveryPreferencesButton>
            </SaveCancelContainer>
          )}
        </>
      )}
    </GoPaperlessContainer>
  );
};

DeliveryPreferences.propTypes = {
  dispatch: PropTypes.func,
  i18n: PropTypes.object,
  isLoading: PropTypes.bool,
  customer: PropTypes.object,
  emailAddress: PropTypes.string,
  formErrors: PropTypes.string,
  profileName: PropTypes.string,
  profileMailingAddress: PropTypes.object,
  phoneNumberPrimary: PropTypes.string,
  phoneNumberSecondary: PropTypes.string,
  phoneNumberPrimaryName: PropTypes.object,
  phoneNumberSecondaryName: PropTypes.object,
  phoneErrors: PropTypes.any,
  emailErrors: PropTypes.any,
  editingEmailAndPhone: PropTypes.bool,
  primaryPhoneNameType: PropTypes.string,
  secondaryPhoneNameType: PropTypes.string,
  state: PropTypes.object,
  customerParty: PropTypes.object,
  apiResponseSizeLimit: PropTypes.number,
};
const mapStateToProps = (state) => ({
  apiResponseSizeLimit: state.configurationSlice.features.apiResponseSizeLimit,
  i18n: state.i18nSlice.i18n,
  isLoading: state.deliveryPreferencesSlice.isLoading,
  policies: state.policySlice.policies,
  statementAccounts: state.deliveryPreferencesSlice.statementAccounts,
  systemId: state.customerSlice.customer.systemId,
  customer: state.customerSlice.customer, //do not remove: used in patch customer
  billingAccounts: state.billingSlice.billingAccounts,
  customerAddress: getMailingAddress(state),
  customerParty: getCustomerParty(state),
  customerEmail: getCustomerParty(state).emailInfo.emailAddr,
  customerId: state.loginSlice.user.customer_id,
  desktopDeliveryPreferencesDefaultExpanded:
    state.configurationSlice.features.features.desktopDeliveryPreferencesDefaultExpanded,
  createdSinceDate: state.configurationSlice.features.policyCreatedSinceDt,
  mobileDeliveryPreferencesDefaultExpanded:
    state.configurationSlice.features.features.mobileDeliveryPreferencesDefaultExpanded,
  policyStatusCd: state.configurationSlice.features.policyStatusCd,
  policiesCountLimit: state.configurationSlice.features.policiesCountLimit,
});

export default connect(mapStateToProps)(DeliveryPreferences);
